import { PersonAvatar } from "client/Components";
import { getComputedDisplayName } from "client/Features/AddressBook/common";
import { AddressBookContactSource } from "client/Features/AddressBook/Overview/Overview";
import { presenceToAvatarStatusMap } from "client/Features/AddressBook/PersonCard/PersonCard";
import { ContactListItem, ContactPresenceStatus } from "client/Api/Email/contacts";
import { generateTrackingId } from "client/Misc/utils";
import { ReactElement, useCallback, useContext } from "react";
import { ListItem } from "../../../ListItem";
import { TileContext } from "../../../TileContext";
import { CommunicationActions } from "./CommunicationActions";
import { ResponsiveCommunicationActions } from "./ResponsiveCommunicationActions";

export type ContactProps = {
	contact: ContactListItem;
	source: AddressBookContactSource;
	presenceStatus: ContactPresenceStatus;
	onOpenContact(contactId: string, source: AddressBookContactSource): void;
};

export const Contact = (props: ContactProps): ReactElement => {
	const {
		contact,
		source,
		presenceStatus,
		onOpenContact
	} = props;

	const {
		dimension
	} = useContext(TileContext);

	const isNarrow: boolean = dimension.kind === "responsive"
		|| dimension.dimension.columnSpan <= 6;

	const openContactClickHandler = useCallback(
		(): void => {
			onOpenContact(contact.id, source);
		},
		[
			contact.id,
			source,
			onOpenContact
		]
	);

	const {
		computedDisplayName,
		initials
	} = getComputedDisplayName(contact);

	return (
		<ListItem
			hasSeparateClickableAreas
			trackingId={generateTrackingId("contact", `|${contact.id}`)}
			isHoverable
		>
			<ListItem.ClickableArea
				onClick={openContactClickHandler}
				position="first"
				shouldGrow
				trackingId="open-contact"
				tooltip={computedDisplayName}
			>
				<ListItem.Icon>
					<PersonAvatar
						initials={initials}
						imageSrc={contact.photoUrl}
						size="small"
						trackingId="avatar"
						status={{
							kind: "status",
							status: presenceToAvatarStatusMap[presenceStatus]
						}}
					/>
				</ListItem.Icon>
				<ListItem.Content>
					<ListItem.Row>
						<ListItem.Title trackingId="display-name">
							{computedDisplayName}
						</ListItem.Title>
					</ListItem.Row>
				</ListItem.Content>
			</ListItem.ClickableArea>
			{isNarrow
				? <ResponsiveCommunicationActions contact={contact} />
				: <CommunicationActions contact={contact} />
			}
		</ListItem>
	);
};
Contact.displayName = "ContactListItem";